import React, {Component, Fragment} from 'react';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import Clock from 'react-feather/dist/icons/clock.js';
import Calendar from 'react-feather/dist/icons/calendar.js';
import Box from 'react-feather/dist/icons/box.js';
import DollarSign from 'react-feather/dist/icons/dollar-sign.js';
import MapPin from 'react-feather/dist/icons/map-pin.js';
import Award from 'react-feather/dist/icons/award.js';
import Mail from 'react-feather/dist/icons/mail.js';
import Phone from 'react-feather/dist/icons/phone.js';
import {SubmissionError} from 'redux-form';
import { format } from 'date-fns';
import {Badge} from 'reactstrap';
import {Link} from 'react-router-dom';

import Skills from '../../../../components/Skills/Skills.jsx';
import MomentDate from '../../../../../shared/components/Moment/Date.jsx';
import DateTime from '../../../../../shared/components/Moment/Datetime.jsx';
import OfferAnswerModal from '../../../../components/Modals/OfferAnswerModal/OfferAnswerModal.jsx';
import OfferAnswerDenialModal from '../../../../components/Modals/OfferAnswerDenialModal/OfferAnswerDenialModal.jsx';
import MessageModal from '../../../../../shared/components/Modals/MessageModal/MessageModal.jsx';
import BlueCheck from '../../../../components/BlueCheck/BlueCheck.jsx';
import OfferAnswersList from '../../../../components/OfferAnswers/OfferAnswersList/OfferAnswersList.jsx';
import SubscribeBanner from '../../../../components/SubscribeBanner/SubscribeBanner.jsx';
import Picture from '../../../../components/Picture/Picture.jsx';
import mustBeLogged from '../../../../components/Must/MustBeLogged.jsx';
import OfferLayout from '../OfferLayout/OfferLayout.jsx';

import api from '../../../../../api.js';
import conf from '../../../../conf.js';
import connectModal from '../../../../../shared/components/Modals/connect.js';
import {generate} from '../../../../routes.js';
import sharedConf from '../../../../../shared/conf.js';

import './OfferDetails.scss';

class OfferDetails extends Component {
  static propTypes = {
    currentUser: PropTypes.object,
    className: PropTypes.string,
    offer: PropTypes.object.isRequired,
    withLayout: PropTypes.bool,
  };

  static defaultProps = {
    className: '',
    withLayout: false,
  };

  constructor(props) {
    super(props);
    this.state = {
      dropdownOpen: false,
      offerAnswers: props?.offer.offerAnswers?.length ? props.offer.offerAnswers : [],
    }
  }

  componentDidUpdate(prevProps, prevState, prevContext) {
    if (JSON.stringify(this.props?.offer.offerAnswers?.length ? this.props.offer.offerAnswers : []) !== JSON.stringify(prevProps?.offer.offerAnswers?.length ? prevProps.offer.offerAnswers : [])) {
      this.setState({
        offerAnswers: this.props.offerAnswers
      });
    }
  }

  applyForJob = () => {
    if (this.props?.currentUser.complete) {
      this.props.openOfferAnswerModal({
        user: this.props.currentUser,
        freelance: this.props.freelance,
        offer: this.props.offer,
        answer: {},
        onClose: () => {
          this.props.refresh();
        }
      })
    } else {
      this.props.openMessageModal({
        title: 'Vous n\'avez pas finalisé votre inscription',
        message: 'Vous devez finaliser votre inscription avant de postuler à une annonce',
        actions: [
          {
            text: 'Fermer',
            className: 'btn-primary',
            onClick: (modal) => {
              modal.toggle();
            }
          }
        ]
      });
    }
  };

  edit = (answer, offset) => {
    this.props.openOfferAnswerModal({
      user: this.props.currentUser,
      freelance: this.props.freelance,
      offer: this.props.offer,
      answer,
      offset,
      onClose: async () => {
        this.setState({
          offerAnswers: (await api.offers.answers.getAll({offer: this.props.offer.id})).data
        });
      }
    })
  };

  deny = (answer, offset) => {
    this.props.openOfferAnswerDenialModal({
      answer,
      offer: this.props.offer,
      onClose: async () => {
        this.setState({
          offerAnswers: (await api.offers.answers.getAll({offer: this.props.offer.id})).data
        });
      }
    });
  };

  denyAllAndClose = async () => {
    const activeOfferAnswers = this.props.offer.offerAnswers.filter((offerAnswer) => {
      return isNaN(offerAnswer.denial_motive);
    });
    try {
      const offer = this.props.offer.id;
      await Promise.all(activeOfferAnswers.map((offerAnswer) => {
        return api.offers.answers.edit({
          denialMotive: 5,
          offerAnswer: offerAnswer.id,
          offer: offer
        });
      }));
      const response = await api.offers.edit({
        offer: offer,
        filled: true,
        filled_date: format(new Date(), "yyyy-MM-dd'T'HH:mm:ss.SSSXXX")
      });
      await this.props.refresh(response.data);
    } catch (e) {
      console.warn(e);
      throw new SubmissionError(e);
    }
  };

  remove = (item, offset) => {
    this.props.openMessageModal({
      title: 'Êtes-vous sûr de vouloir supprimer votre candidature ?',
      message: 'Attention, cette action est irréversible',
      actions: [
        {
          text: 'Supprimer quand même',
          className: 'btn-secondary mr-4',
          onClick: async (modal) => {
            modal.toggle();
            try {
              await api.offers.answers.remove({
                offer: this.props.offer.id,
                answer: item.id,
              });
              this.props.refresh();
            } catch (e) {
              console.warn(e);
            }
          }
        },
        {
          text: 'Annuler',
          className: 'btn-primary',
          onClick: (modal) => {
            modal.toggle();
          }
        }
      ]
    });
  };

  render() {
    const {currentUser, offer} = this.props;
    const isInternal = currentUser?.isInternal;
    const isAdmin = currentUser?.isAdmin || currentUser?.isBusinessManager;

    let canApplyForJob = false;
    if (currentUser && currentUser.cgu_accepted) {
      if (currentUser.account_type === sharedConf.accountTypes.freelance && currentUser.mainUniverse === offer.universe && !offer.hasAnswer && !offer.filled && !offer.locked) {
        canApplyForJob = true;
      }
      if (isAdmin) {
        canApplyForJob = true;
      }
    }

    const activeOfferAnswers = offer.offerAnswers.filter((offerAnswer) => {
      return isNaN(offerAnswer.denial_motive);
    });
    const activeMails = activeOfferAnswers.map((offerAnswer) => offerAnswer.email);

    const offerDetails = <div className="offer-details">
      <Helmet>
        <title>Annonces {offer?.title ?? 'Titre de l\'annonce'}</title>
      </Helmet>
      <div className="row bg-gray-100">
        <div className="col-xxl-12 p-3 text-center">
          Vous voulez en savoir plus ? Contactez-nous au <a
            href={`tel:${conf.site.phone}`}>{conf.site.phone}</a>
        </div>
      </div>
      <div className="row intro">
        {
          offer?.hasAnswer &&
          <div className="check-right">
            <BlueCheck
              hasTooltip
              tooltipOverlay="J'ai postulé"
            />
          </div>
        }
        <div className="col-xxl-12 title">
          <h1>{offer?.title ?? 'Titre de l\'annonce'}</h1>
          <div className="d-flex align-items-center">
            <strong>Mission ID :</strong> #{offer?.id}&nbsp;
          </div>
          {isInternal &&
          <div className="date">
            {offer?.publication_date &&
            <div className="publication">Publiée le <DateTime date={offer?.publication_date ?? null}/></div>}
            {offer?.filled &&
            <div className="filled">Pourvue le <DateTime date={offer?.filled_date ?? null}/></div>}
          </div>
          }
          {isAdmin && (offer?.sms_ticket?.nb_sendings ?? 0) !== 0 ?
            <div className="sms">
              SMS envoyé à {offer?.sms_ticket?.nb_sendings ?? 0} destinataire(s)
            </div> : ''
          }
        </div>
        <div className="col-xxl-12 details">
          <div className="row">
            <div className="col-md-4">
              <div className="item">
                <MapPin/> &nbsp; {offer?.city?.[0]?.departement ?? 'Non précisé'}<br/>
              </div>
              <div className="item">
                <Award/> &nbsp; {offer?.experiences ?? 'Non précisé'}<br/>
              </div>
            </div>
            <div className="col-md-4">
              <div className="item">
                <Calendar/> &nbsp; <MomentDate date={offer?.start_date ?? null}/><br/>
              </div>
              <div className="item">
                <Clock/> &nbsp; {offer?.duration ?? 'Non précisé'}<br/>
              </div>
            </div>
            <div className="col-md-4">
              <div className="item">
                <DollarSign/> &nbsp; {offer?.tjm_text ?? 'Non précisé'}<br/>
              </div>
              <div className="item">
                <Box/> &nbsp; {offer?.secteurs?.[0]?.name ?? 'Non précisé'}<br/>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="row bg-gray-100 actions">
        <div className="col-xxl-12 d-flex">
          <Skills
            key={offer.id}
            linkName="offers.search.results"
            list={(offer?.keywords?.length ? offer.keywords : [])}
          />
          <div className="ml-auto">
            {isAdmin ? <button className="btn btn-danger btn-sm" onClick={() => this.props.goToEdit(offer)}>Éditer</button> : ''}
            {canApplyForJob ? <button className="btn btn-secondary btn-sm" onClick={this.applyForJob}>Postuler</button> : ''}
            {offer.filled ? <Badge className="badge badge-danger">Cette annonce a déjà été pourvue</Badge> : ''}
            {offer.locked ? <Badge className="badge badge-danger">Candidatures fermées</Badge> : ''}
            {!currentUser ?
              <Link className="btn btn-secondary btn-sm" to={generate({name: 'register-freelance'})}>
                Voir plus de missions
              </Link> : ''
            }
          </div>
        </div>
      </div>
      {offer.manager &&
      <div className="row justify-content-center mb-4 bg-gray-100 py-4 manager">
        <h2 className="col-12 text-center">Responsable de l'offre</h2>
        <div className="row align-items-center justify-content-center">
          <div className="col-4 col-md-2">
            <Picture
              src={offer.manager?.image.square ?? null}
              alt={offer.manager.fullname}
              className="picture"
            />
          </div>
          <div className="text-center text-sm-left p-xs-4 p-2">
            <h3 className="name">{offer.manager.fullname}</h3>
            <p className="d-flex align-items-center mb-0">
              <Mail/>&nbsp;
              <a href={`mailto:${offer.manager.email}`}>
                {offer.manager.email}
              </a>
            </p>
            {offer.manager.tel &&
            <p className="d-flex align-items-center mb-0">
              <Phone/>&nbsp;
              <a href={`tel:${offer.manager.tel}`}>
                {offer.manager.tel}
              </a>
            </p>
            }
          </div>
        </div>
      </div>
      }
      {!currentUser && <SubscribeBanner target={conf.search.targets.offers}/>}
      <div className="row description">
        <div
          className="col-xxl-12"
          dangerouslySetInnerHTML={{__html: offer?.description ?? 'Sans description'}}
        />
      </div>
      {
        <div>
          {
            ((isInternal || (currentUser && currentUser.account_type === sharedConf.accountTypes.freelance && !offer.filled)) &&
              <div className="row bg-gray-100 align-items-center actions">
                <div className="col-sm-12 col-md-4">
                  {
                    offer.nbOfferAnswer > 0 ?
                      (
                        offer.nbOfferAnswer > 1 ?
                          <div> {offer.nbOfferAnswer} personnes ont postulé à cette annonce</div>
                          :
                          <div> 1 personne a postulé à cette annonce</div>
                      )
                      :
                      <div>Soyez le premier a postuler à cette offre</div>
                  }
                </div>
                <div className="col-sm-12 col-md-8 text-right">
                  {isAdmin &&
                  <Fragment>
                    <a className="btn btn-secondary" href={`mailto:?bcc=${activeMails.join(';')}`}>
                      Écrire à tous, en attente
                    </a>&nbsp;
                    <a className="btn btn-secondary" href={`mailto:?bcc=${activeMails.join(';')}`}
                      onClick={this.denyAllAndClose}>
                      Suite mission + cloture
                    </a>&nbsp;
                  </Fragment>
                  }
                  {canApplyForJob &&
                  <button className="btn btn-secondary" onClick={this.applyForJob}>Postuler</button>
                  }
                </div>
              </div>)
          }
          {(isInternal || offer.hasAnswer) &&
          <div className="row">
            <div className="col-xxl-12">
              <OfferAnswersList
                isAdmin={isAdmin}
                list={this.state.offerAnswers}
                edit={this.edit}
                deny={this.deny}
                remove={this.remove}
              />
            </div>
          </div>
          }
        </div>
      }
    </div>;

    if (this.props.withLayout) {
      return <OfferLayout>
        {offerDetails}
      </OfferLayout>
    }

    return offerDetails;
  }
}

export default connectModal(
  {MessageModal, OfferAnswerDenialModal, OfferAnswerModal},
  mustBeLogged(OfferDetails)
);